import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { initiateSignUp } from '@/lib/api';
import { InlineNotification, MicrosoftSignInButton, TextInput } from '@/components/carbon';
import { FrameNote, PUBLIC_LINK, SignInFrame } from '@/components/PublicLayout';

interface FormData {
  customerEmail: string;
  customerName: string;
  companyName: string;
}

interface FormErrors {
  customerEmail?: string;
  customerName?: string;
  companyName?: string;
}

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    customerEmail: '',
    customerName: '',
    companyName: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'Enter your work email.';
    } else if (!validateEmail(formData.customerEmail)) {
      newErrors.customerEmail = 'Enter a valid email address, like admin@yourcompany.com.';
    }

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Enter your full name.';
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = 'Use at least 2 characters for your name.';
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Enter your company name.';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Use at least 2 characters for the company name.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await initiateSignUp({
        customerEmail: formData.customerEmail.trim(),
        customerName: formData.customerName.trim(),
        companyName: formData.companyName.trim(),
      });

      if (response.success && response.sessionId && response.consentUrl) {
        // Store sessionId in localStorage for later retrieval
        localStorage.setItem('guardian_signup_session', response.sessionId);
        // Redirect to Microsoft admin consent page
        window.location.href = response.consentUrl;
      } else {
        setApiError(response.error || 'Guardian could not start your sign-up. Try again in a moment.');
      }
    } catch (err) {
      setApiError(
        err instanceof Error
          ? err.message
          : 'Something unexpected stopped your sign-up. Try again in a moment.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SignInFrame
      brandTo="/signup"
      headline="Request access for your organization."
      lede="Tell us who you are, then a Global Administrator grants consent on Microsoft’s own screen."
    >
      {apiError && (
        <InlineNotification
          kind="error"
          title="Sign-up didn’t start."
          onClose={() => setApiError(null)}
          className="mb-6"
        >
          {apiError}
        </InlineNotification>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <TextInput
          type="email"
          id="customerEmail"
          name="customerEmail"
          label="Work email"
          autoComplete="email"
          value={formData.customerEmail}
          onChange={handleInputChange}
          placeholder="admin@yourcompany.com"
          error={errors.customerEmail}
          disabled={isLoading}
        />
        <TextInput
          type="text"
          id="customerName"
          name="customerName"
          label="Full name"
          autoComplete="name"
          value={formData.customerName}
          onChange={handleInputChange}
          placeholder="John Doe"
          error={errors.customerName}
          disabled={isLoading}
        />
        <TextInput
          type="text"
          id="companyName"
          name="companyName"
          label="Company name"
          autoComplete="organization"
          value={formData.companyName}
          onChange={handleInputChange}
          placeholder="Contoso Inc"
          error={errors.companyName}
          disabled={isLoading}
        />
        <div className="mt-2">
          {/* Submitting opens Microsoft's admin consent screen: Microsoft's own button (DESIGN.md). */}
          <MicrosoftSignInButton type="submit" label="Continue with Microsoft" busy={isLoading} />
          {/* OB-05: the sign-up state lasts 15 minutes (SignUpService.StateExpiryMinutes). */}
          <p className="m-0 mt-2 text-helper text-text-helper">
            Finish Microsoft&rsquo;s consent screen within 15 minutes of continuing. After that the link
            expires and you start the sign-up again.
          </p>
          <p className="m-0 mt-2 text-helper text-text-helper">
            We only collect your name, email and company to set up your account. Guardian reads your
            Microsoft&nbsp;365 configuration and directory details, such as user names, sign-in activity and MFA
            status, to watch for risky changes. It doesn&rsquo;t change your policies or settings, and it
            doesn&rsquo;t read email or files.{' '}
            <Link to="/docs/permissions" className={PUBLIC_LINK}>
              Learn more about permissions
            </Link>
          </p>
          <p className="m-0 mt-2 text-helper text-text-helper">
            Your tenant needs Microsoft Entra ID P1 or P2, on its own or in a plan that includes it, such as
            Microsoft&nbsp;365 E3 or E5 or Business Premium, because Microsoft&rsquo;s configuration monitoring
            requires it. GCC High and DoD tenants aren&rsquo;t supported, and GCC tenants haven&rsquo;t been tested
            yet.
          </p>
        </div>
      </form>

      <FrameNote>
        <p className="m-0">
          Already have an account?{' '}
          <Link to="/dashboard" className={PUBLIC_LINK}>
            Sign in
          </Link>
        </p>
      </FrameNote>
    </SignInFrame>
  );
};

export default SignUp;
